import * as React from "react"
import {
  StructuredLogLevel,
  BuildStatus,
  StructuredLog,
  BuildRunnerType,
} from "@modules/graphql/types"
import { navigate } from "gatsby"
import { useLocation } from "@reach/router"
import {
  StickyObserver,
  NumberBadge,
  NumberBadgeTone,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  useTabsContext,
} from "gatsby-interface"
import { excludeActivitiesList } from "@modules/build/logs/structured/constants"
import { useStructuredLogs } from "../hooks/useStructuredLogs"
import { StructuredLogsTimeline } from "./StructuredLogsTimeline"
import { useTracker } from "@modules/analytics"
import Loading from "@modules/ui/components/Loading"
import { BuildRawLogsList } from "../../raw/components/BuildRawLogsList"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"

export type StructuredLogsTabsProps = {
  buildId: string
  siteId: string
  organizationId: string
  runnerType: BuildRunnerType
  buildStatus: BuildStatus
}

const LogsTypeHashes = [`all`, `errors`, `warnings`, `rawLogs`]

export function StructuredLogsTabs({
  buildId,
  siteId,
  organizationId,
  buildStatus,
  runnerType,
}: StructuredLogsTabsProps) {
  const { trackButtonClicked } = useTracker()
  const location = useLocation()
  const locationHash = location?.hash.replace(`#`, ``)
  const tabIndex =
    locationHash && LogsTypeHashes.includes(locationHash)
      ? LogsTypeHashes.indexOf(locationHash)
      : 0

  const [
    structuredLogsForBuild,
    { loading, error, refetch },
  ] = useStructuredLogs({
    buildId,
    siteId,
    logLevel: StructuredLogLevel.All,
  })

  const logTabs = getStructuredLogsTabs(structuredLogsForBuild)
  const showRawLogs = ![BuildStatus.Canceled, BuildStatus.Queued].includes(
    buildStatus
  )

  const handleOnChange = (activeTab: number) => {
    trackRawLogsTab(activeTab)

    navigate(`${location.pathname}#${LogsTypeHashes[activeTab]}`)
  }

  const trackRawLogsTab = (activeTab: number) => {
    // "Raw logs" tab is always going to be right after structured logs
    // so we can identify it by comparing activeTab with the length of tabs for structured logs
    if (activeTab !== logTabs.length) {
      return
    }

    trackButtonClicked(`View raw log`, {
      organizationId,
      uiSource: `Build/Preview list`,
      buildId,
    })
  }

  if (error) {
    return <ErrorAlert>{error.message}</ErrorAlert>
  }

  if (loading) {
    return <Loading message="Loading the logs..." />
  }

  return (
    <React.Fragment>
      <Tabs onChange={handleOnChange} index={tabIndex}>
        <StickyObserver
          lipShadowPosition="bottom"
          css={theme => ({ backgroundColor: theme.colors.white })}
        >
          <TabList>
            {logTabs.map(({ level, label, count }, tabIndex) => {
              return (
                <Tab key={level}>
                  {label}
                  {typeof count === "number" && (
                    <StructuredLogsTabBadge
                      count={count}
                      level={level}
                      tabIndex={tabIndex}
                    />
                  )}
                </Tab>
              )
            })}
            {showRawLogs && <Tab>Raw logs</Tab>}
          </TabList>
        </StickyObserver>
        <TabPanels css={theme => ({ paddingTop: theme.space[7] })}>
          {logTabs.map(({ level, logs }) => {
            return (
              <TabPanel key={level}>
                <StructuredLogsTimeline
                  structuredLogs={logs}
                  refetch={refetch}
                  logLevel={level}
                  buildStatus={buildStatus}
                  runnerType={runnerType}
                />
              </TabPanel>
            )
          })}
          {showRawLogs && (
            <TabPanel>
              <BuildRawLogsList buildId={buildId} siteId={siteId} />
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    </React.Fragment>
  )
}

const ignoredActivities = excludeActivitiesList.reduce<Record<string, boolean>>(
  (merged, name) => {
    merged[name] = true
    return merged
  },
  {}
)

const isErrorLog = ({ level }: StructuredLog) =>
  level === StructuredLogLevel.Error
const isWarningLog = ({ level }: StructuredLog) =>
  level === StructuredLogLevel.Warning

function getStructuredLogsTabs(structuredLogsForBuild: StructuredLog[]) {
  const allLogs = structuredLogsForBuild.filter(log => {
    if (log.activity?.name && ignoredActivities[log.activity?.name]) {
      return false
    }

    return true
  })

  const errorLogs = allLogs.filter(isErrorLog)
  const warningLogs = allLogs.filter(isWarningLog)

  return [
    {
      level: StructuredLogLevel.All,
      label: `All Logs`,
      logs: allLogs,
      count: null,
    },
    {
      level: StructuredLogLevel.Error,
      label: `Errors`,
      logs: errorLogs,
      count: errorLogs.length,
    },
    {
      level: StructuredLogLevel.Warning,
      label: `Warnings`,
      logs: warningLogs,
      count: warningLogs.length,
    },
  ]
}

type StructuredLogsTabBadgeProps = {
  count: number
  level: string
  tabIndex: number
}

function StructuredLogsTabBadge({
  count,
  level,
  tabIndex,
}: StructuredLogsTabBadgeProps) {
  const { selectedIndex } = useTabsContext()

  let tone: NumberBadgeTone = `NEUTRAL`
  // We only want to apply non-neutral tone to inactive tabs
  if (tabIndex !== selectedIndex && count > 0) {
    tone = level === StructuredLogLevel.Error ? `DANGER` : `WARNING`
  }

  return (
    <NumberBadge tone={tone} css={theme => ({ marginLeft: theme.space[3] })}>
      {count}
    </NumberBadge>
  )
}
